import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  // DialogContent,
  // DialogContentText,
  DialogTitle,
  Divider,
  Grid2,
  Stack,
  Typography,
} from "@mui/material";
import { IconCheck, IconLinkOff, IconAlertCircle } from "@tabler/icons-react";
// import { IconChevronDown, IconLink } from "@tabler/icons-react";

import { getMpDisconnect, getMpStatus } from "../services/finance";
// import { getMpAuthUrl } from "../services/finance";
import { formatDate } from "../helpers/format";

const MercadoPagoTab = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Busca status da conexão
  const {
    data: status,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: ["mp-status"],
    queryFn: getMpStatus,
  });

  // Fluxo de conexão mantido para reativação futura.
  // const { mutate: startConnect, isPending: isPendingConnect } = useMutation({
  //   mutationFn: getMpAuthUrl,
  //   onSuccess: ({ url }) => {
  //     window.location.href = url;
  //   },
  //   onError: () => {
  //     toast.error("Não foi possível iniciar a conexão. Tente novamente.");
  //   },
  // });

  // Desconecta
  const { mutate: disconnect, isPending: isPendingDisconnect } = useMutation({
    mutationFn: getMpDisconnect,
    onSuccess: () => {
      setConfirmOpen(false);
      refetchStatus();
      toast.success("Conta Mercado Pago desvinculada.");
    },
    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(e?.response?.data?.message || "Erro ao desvincular conta.");
    },
  });

  if (isLoadingStatus) {
    return (
      <Grid2 size={12} display="flex" justifyContent="center" py={4}>
        <CircularProgress size={28} />
      </Grid2>
    );
  }

  {
    /* Modal de confirmação de desconexão */
  }
  return (
    <>
      <Grid2 size={12}>
        <Stack
          sx={{ backgroundColor: "secondary.dark", borderRadius: 2, p: 2 }}
          gap={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack>
              <Typography fontSize={{ xs: 16, sm: 18 }} fontWeight={700}>
                Integração Mercado Pago
              </Typography>
              <Typography
                fontSize={{ xs: 12, sm: 14 }}
                color="secondary.contrastText"
              >
                Estamos finalizando as atividades e encerrando a operação de
                pagamentos. Caso sua conta esteja vinculada, você pode apenas
                desvincular abaixo.
              </Typography>
            </Stack>

            {status?.connected ? (
              <Chip
                icon={<IconCheck size={14} />}
                label="Conectado"
                color="success"
                size="small"
              />
            ) : (
              <Chip
                icon={<IconAlertCircle size={14} />}
                label="Não conectado"
                variant="outlined"
                size="small"
              />
            )}
          </Stack>

          <Divider />

          {status?.connected ? (
            <ConnectedState
              mpUserId={status.mpUserId}
              connectedAt={status.connectedAt}
              onDisconnect={() => setConfirmOpen(true)}
              isPending={isPendingDisconnect}
            />
          ) : (
            <DisconnectedState />
          )}

          {/*
          <Divider />
          <FaqSection />
          */}
        </Stack>
      </Grid2>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Desvincular Mercado Pago</DialogTitle>
        {/* <DialogContent>
          <DialogContentText>
            Tem certeza que deseja desvincular sua conta do Mercado Pago? Os
            pagamentos via MP serão interrompidos imediatamente.
          </DialogContentText>
        </DialogContent> */}
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => disconnect()}
            disabled={isPendingDisconnect}
          >
            {isPendingDisconnect ? (
              <CircularProgress size={16} />
            ) : (
              "Desvincular"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ── Sub-componentes ───────────────────────────────────────────

const DisconnectedState = () => (
  <Stack gap={2}>
    <Alert severity="info" icon={<IconAlertCircle size={18} />}>
      Não existe conta Mercado Pago vinculada no momento. O sistema está em
      processo de encerramento e novas conexões estão sendo desativadas.
    </Alert>

    {/*
    <Box>
      <Button
        variant="contained"
        startIcon={
          isPendingConnect ? <CircularProgress size={14} /> : <IconLink size={16} />
        }
        onClick={() => startConnect()}
        disabled={isPendingConnect}
      >
        Conectar Mercado Pago
      </Button>
    </Box>
    */}
  </Stack>
);

const ConnectedState = ({
  mpUserId,
  connectedAt,
  onDisconnect,
  isPending,
}: {
  mpUserId: string | null;
  connectedAt: string | null;
  onDisconnect: () => void;
  isPending: boolean;
}) => (
  <Stack gap={2}>
    <Alert severity="warning" icon={<IconAlertCircle size={18} />}>
      Estamos finalizando as atividades. Se desejar, desvincule sua conta do
      Mercado Pago.
    </Alert>

    <Stack gap={0.5}>
      {mpUserId && (
        <Typography fontSize={13} color="secondary.contrastText">
          ID da conta MP: <strong>{mpUserId}</strong>
        </Typography>
      )}
      {connectedAt && (
        <Typography fontSize={13} color="secondary.contrastText">
          Conectado em: <strong>{formatDate(connectedAt)}</strong>
        </Typography>
      )}
    </Stack>

    <Box>
      <Button
        variant="outlined"
        color="error"
        startIcon={
          isPending ? <CircularProgress size={14} /> : <IconLinkOff size={16} />
        }
        onClick={onDisconnect}
        disabled={isPending}
      >
        Desvincular conta
      </Button>
    </Box>
  </Stack>
);

export default MercadoPagoTab;

/*
// FAQ mantida para futura reativação.
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { IconChevronDown } from "@tabler/icons-react";

const faqs = [
  {
    question: "Como faço para conectar minha conta Mercado Pago?",
    answer:
      'Clique no botão "Conectar Mercado Pago" acima. Você será redirecionado para o site do Mercado Pago para autorizar o acesso. Após confirmar, voltará automaticamente para o painel com a conta vinculada.',
  },
  {
    question: "Quais formas de pagamento são aceitas?",
    answer:
      "Com a integração ativa, seus alunos podem pagar via Pix (QR Code e Copia e Cola) e cartão de crédito diretamente na tela de checkout da cobrança.",
  },
  {
    question: "O dinheiro cai direto na minha conta Mercado Pago?",
    answer:
      "Sim. Todo o valor é recebido diretamente na sua conta Mercado Pago, sem intermediários. Você pode transferir para sua conta bancária quando quiser pelo app ou site do Mercado Pago.",
  },
];

const FaqSection = () => (
  <Stack gap={1}>
    <Typography fontSize={14} fontWeight={700}>
      Dúvidas frequentes
    </Typography>
    {faqs.map((faq) => (
      <Accordion
        key={faq.question}
        disableGutters
        elevation={0}
        sx={{
          backgroundColor: "transparent",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px !important",
          "&:before": { display: "none" },
          "&.Mui-expanded": { borderColor: "primary.main" },
        }}
      >
        <AccordionSummary
          expandIcon={<IconChevronDown size={16} />}
          sx={{ minHeight: 44, "& .MuiAccordionSummary-content": { my: 0.5 } }}
        >
          <Typography fontSize={13} fontWeight={600}>
            {faq.question}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography fontSize={13} color="text.secondary">
            {faq.answer}
          </Typography>
        </AccordionDetails>
      </Accordion>
    ))}
  </Stack>
);
*/
